import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { LineChart, Line, Area, XAxis, YAxis, ReferenceLine, ReferenceArea, ResponsiveContainer, Tooltip, CartesianGrid, Scatter, ComposedChart } from "recharts";
import { api, formatBg, formatDelta, DIRECTION_ARROWS, DIRECTIONS, bgStatus, statusColor, statusLabel } from "../lib/glucose";
import { useAuth } from "../lib/auth";
import { evaluateReading } from "../lib/alerts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { Plus, ArrowClockwise, Users, CalendarBlank, X } from "@phosphor-icons/react";
import { useSearchParams, Link } from "react-router-dom";

const RANGES = [
  { value: 3, label: "3h" },
  { value: 6, label: "6h" },
  { value: 12, label: "12h" },
  { value: 24, label: "24h" },
];

function formatTime(ms, hours) {
  const d = new Date(ms);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  if (hours >= 24 * 7) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  if (hours >= 24) {
    return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
  }
  return `${hh}:${mm}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get("patient");
  const [hours, setHours] = useState(6);
  const [selectedDate, setSelectedDate] = useState(null); // Date object; when set, view that specific day (00:00-23:59)
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [current, setCurrent] = useState(null);
  const [stats, setStats] = useState(null);
  const [watchers, setWatchers] = useState([]);
  const [sensor, setSensor] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [iobSeries, setIobSeries] = useState([]);
  const [showIob, setShowIob] = useState(() => localStorage.getItem("gc.showIob") === "1");
  const [addOpen, setAddOpen] = useState(false);
  const [sgvInput, setSgvInput] = useState("");
  const [directionInput, setDirectionInput] = useState("Flat");
  const [loading, setLoading] = useState(true);

  const units = user?.units || "mg/dL";
  const targetLow = user?.target_low || 80;
  const targetHigh = user?.target_high || 160;
  const alertLow = user?.low_threshold || 70;
  const alertHigh = user?.high_threshold || 180;

  // Compute [since, until] window for the chart & stats query.
  // If a specific calendar day is selected, use that day's 00:00..23:59 in local time.
  // Otherwise, use rolling [now - hours, now].
  const window = useMemo(() => {
    if (selectedDate) {
      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(23, 59, 59, 999);
      return { since: start.getTime(), until: end.getTime(), effectiveHours: 24 };
    }
    const now = Date.now();
    return { since: now - hours * 3600 * 1000, until: now, effectiveHours: hours };
  }, [selectedDate, hours]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = targetUserId ? { target_user_id: targetUserId } : {};
      const rangeParams = { since: window.since, until: window.until };
      const [e, c, s, w] = await Promise.all([
        api.get("/entries", { params: { ...params, ...rangeParams } }),
        api.get("/entries/current", { params }),
        api.get("/stats", { params: { ...params, ...rangeParams } }),
        api.get("/watchers"),
      ]);
      setEntries(e.data || []);
      setCurrent(c.data || null);
      setStats(s.data || null);
      setWatchers(w.data || []);
      // Sensor status (best-effort; ignore errors)
      try {
        const sr = await api.get("/sensors/current", { params });
        setSensor(sr.data || null);
      } catch { setSensor(null); }
      // Predictions (IOB/COB) — only for live view of own data
      if (!selectedDate) {
        try {
          const pr = await api.get("/predictions", { params });
          setPredictions(pr.data || null);
        } catch { setPredictions(null); }
      } else {
        setPredictions(null);
      }
      // IOB time series for chart overlay + optional BG projection
      try {
        const sr = await api.get("/predictions/series", {
          params: {
            ...params,
            since: window.since,
            until: window.until,
            step_min: 15,
            // Only project forward on the live view (not historical)
            project_min: selectedDate ? 0 : 60,
          },
        });
        setIobSeries(sr.data?.series || []);
      } catch { setIobSeries([]); }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [window.since, window.until, targetUserId, selectedDate]);

  useEffect(() => { load(); }, [load]);

  // Auto refresh every 60s — only when viewing "live" (no specific date)
  useEffect(() => {
    if (selectedDate) return; // don't auto-refresh a historical day
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load, selectedDate]);

  // Fire browser alerts when the latest reading crosses a threshold.
  // Only for the logged-in user's own dashboard (not watched patients),
  // and only when the reading timestamp actually advances.
  const lastAlertedDate = useRef(null);
  useEffect(() => {
    if (targetUserId) return;                   // don't alert on someone else's data
    if (selectedDate) return;                   // don't alert on historical view
    if (!current || current.sgv == null) return;
    if (lastAlertedDate.current === current.date) return;
    lastAlertedDate.current = current.date;
    evaluateReading({
      userId: user?.user_id,
      sgv: current.sgv,
      ts: current.date,
      units,
      alertLow, alertHigh, targetLow, targetHigh,
    });
  }, [current, targetUserId, selectedDate, user?.user_id, units, alertLow, alertHigh, targetLow, targetHigh]);

  const addEntry = async () => {
    const val = parseFloat(sgvInput);
    if (!val || val < 20 || val > 600) {
      toast.error("Enter a valid glucose value (20-600 mg/dL)");
      return;
    }
    try {
      // If user input in mmol/L, convert to mg/dL
      const mgdl = units === "mmol/L" ? Math.round(val / 0.0555) : val;
      await api.post("/entries", { sgv: mgdl, direction: directionInput });
      toast.success("Entry added");
      setSgvInput("");
      setAddOpen(false);
      load();
    } catch {
      toast.error("Failed to add entry");
    }
  };

  const chartData = useMemo(() => {
    const sorted = [...entries].sort((a, b) => a.date - b.date);
    if (sorted.length === 0) return [];
    // Collapse readings that share the exact same timestamp (bulk-import artifact)
    // — keep the last one, which corresponds to the most recent value for that ts.
    const deduped = [];
    for (const e of sorted) {
      if (deduped.length && deduped[deduped.length - 1].date === e.date) {
        deduped[deduped.length - 1] = { date: e.date, sgv: e.sgv };
      } else {
        deduped.push({ date: e.date, sgv: e.sgv });
      }
    }
    // Downsample very large series to keep the chart fast.
    // Target ~800 points max: bucket by time and take the mean sgv per bucket.
    const MAX = 800;
    if (deduped.length <= MAX) return deduped;
    const first = deduped[0].date;
    const last = deduped[deduped.length - 1].date;
    const bucketMs = Math.max(1, Math.ceil((last - first) / MAX));
    const buckets = new Map();
    for (const e of deduped) {
      const b = Math.floor((e.date - first) / bucketMs);
      const cur = buckets.get(b);
      if (cur) { cur.sum += e.sgv; cur.n += 1; cur.date = e.date; }
      else buckets.set(b, { sum: e.sgv, n: 1, date: e.date });
    }
    return Array.from(buckets.values()).map((b) => ({ date: b.date, sgv: b.sum / b.n }));
  }, [entries]);

  const currentStatus = current ? bgStatus(current.sgv, alertLow, alertHigh) : "neutral";
  const currentColor = statusColor(currentStatus);
  const currentArrow = current?.direction ? DIRECTION_ARROWS[current.direction] : "";
  const minutesAgo = current ? Math.round((Date.now() - current.date) / 60000) : null;

  const patient = targetUserId ? watchers.find((w) => w.watched_user_id === targetUserId) : null;

  // Chart Y-domain (always mg/dL internally)
  const yMin = 40;
  const yMax = 400;
  const yTick = (v) => (units === "mmol/L" ? (v * 0.0555).toFixed(1) : Math.round(v));

  const RED = "hsl(var(--sig-urgent))";
  const YELLOW = "hsl(var(--sig-high))";
  const GREEN = "hsl(var(--sig-in-range))";

  // Build three per-zone datasets so each Line segment gets the right color.
  // At every threshold crossing between adjacent points, insert an exact
  // crossing point so the colored segments join with no visual gap.
  const zoneData = useMemo(() => {
    const low = alertLow, high = alertHigh;
    if (chartData.length === 0) return { red: [], green: [], yellow: [] };
    const augmented = [chartData[0]];
    for (let i = 1; i < chartData.length; i++) {
      const prev = chartData[i - 1];
      const cur = chartData[i];
      const crossings = [];
      for (const t of [low, high]) {
        const crosses = (prev.sgv < t && cur.sgv > t) || (prev.sgv > t && cur.sgv < t);
        if (crosses) {
          const frac = (t - prev.sgv) / (cur.sgv - prev.sgv);
          crossings.push({
            date: prev.date + frac * (cur.date - prev.date),
            sgv: t,
          });
        }
      }
      crossings.sort((a, b) => a.date - b.date);
      augmented.push(...crossings, cur);
    }
    // For a point exactly ON a threshold we include it in BOTH adjacent zones
    // so their line segments meet exactly at the threshold.
    const red = [], green = [], yellow = [];
    for (const p of augmented) {
      const inRed = p.sgv < low;
      const inGreen = p.sgv >= low && p.sgv <= high;
      const inYellow = p.sgv > high;
      const onLow = p.sgv === low;
      const onHigh = p.sgv === high;
      red.push({ ...p, sgv: inRed || onLow ? p.sgv : null });
      green.push({ ...p, sgv: inGreen ? p.sgv : null });
      yellow.push({ ...p, sgv: inYellow || onHigh ? p.sgv : null });
    }
    return { red, green, yellow };
  }, [chartData, alertLow, alertHigh]);

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {patient && (
        <div className="p-3 rounded-md border border-border bg-secondary/40 flex items-center justify-between" data-testid="viewing-patient-banner">
          <div className="text-sm">
            Viewing <span className="font-medium">{patient.label}</span> ({patient.watched_email})
          </div>
          <Link to="/dashboard" className="text-xs underline text-muted-foreground" data-testid="back-to-my-data">Back to my data</Link>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
            {selectedDate ? "Historical view" : "Live monitoring"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight">Dashboard</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <ToggleGroup
            type="single"
            value={selectedDate ? "" : String(hours)}
            onValueChange={(v) => {
              if (v) {
                setHours(Number(v));
                setSelectedDate(null);
              }
            }}
            className="border border-border rounded-md"
            data-testid="range-toggle"
          >
            {RANGES.map((r) => (
              <ToggleGroupItem key={r.value} value={String(r.value)} data-testid={`range-${r.label}`} className="px-2.5 h-9 text-xs">
                {r.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={selectedDate ? "default" : "outline"}
                className="h-9 gap-1.5 text-xs"
                data-testid="date-picker-btn"
              >
                <CalendarBlank size={14} weight={selectedDate ? "fill" : "regular"} />
                {selectedDate
                  ? selectedDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                  : "Pick a date"}
                {selectedDate && (
                  <X
                    size={12}
                    className="ml-1 opacity-70 hover:opacity-100"
                    onClick={(e) => { e.stopPropagation(); setSelectedDate(null); }}
                    data-testid="clear-date-btn"
                  />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end" data-testid="date-picker-popover">
              <Calendar
                mode="single"
                selected={selectedDate || undefined}
                onSelect={(d) => {
                  if (d) {
                    setSelectedDate(d);
                    setDatePickerOpen(false);
                  }
                }}
                disabled={(d) => d > new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="icon" onClick={load} data-testid="refresh-btn" title="Refresh">
            <ArrowClockwise size={16} />
          </Button>
          {!targetUserId && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-entry-btn" className="gap-1"><Plus size={16} /> Reading</Button>
              </DialogTrigger>
              <DialogContent data-testid="add-entry-dialog">
                <DialogHeader>
                  <DialogTitle>Add glucose reading</DialogTitle>
                  <DialogDescription>Manually record a value from a fingerstick or CGM.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Value ({units})</Label>
                    <Input
                      type="number"
                      step={units === "mmol/L" ? "0.1" : "1"}
                      value={sgvInput}
                      onChange={(e) => setSgvInput(e.target.value)}
                      placeholder={units === "mmol/L" ? "7.2" : "128"}
                      data-testid="sgv-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Trend</Label>
                    <Select value={directionInput} onValueChange={setDirectionInput}>
                      <SelectTrigger data-testid="direction-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DIRECTIONS.map((d) => (
                          <SelectItem key={d} value={d}>{DIRECTION_ARROWS[d]} {d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button onClick={addEntry} data-testid="submit-entry">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
        {/* Current BG hero */}
        <div className="lg:col-span-1 lg:row-span-2 rounded-lg border border-border bg-card p-4 sm:p-6 flex flex-col justify-between min-h-[240px] sm:min-h-[280px]" data-testid="current-bg-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Current</div>
            <div className="flex items-center gap-1.5 text-xs" data-testid="current-status">
              <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: currentColor, color: currentColor }} />
              <span style={{ color: currentColor }}>{statusLabel(currentStatus)}</span>
            </div>
          </div>
          <div>
            <div className="flex items-end gap-3">
              <div className="font-display font-light text-[6.5rem] leading-none tabular" style={{ color: currentColor }} data-testid="current-bg-value">
                {current ? formatBg(current.sgv, units) : "--"}
              </div>
              <div className="pb-4 text-5xl font-display" style={{ color: currentColor }} data-testid="current-bg-arrow">{currentArrow}</div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground tabular">
              {units} ·{" "}
              <span data-testid="current-bg-delta">
                {current?.delta != null ? `${formatDelta(current.delta, units)}` : "--"}
              </span>
              {minutesAgo != null && <> · {minutesAgo}m ago</>}
            </div>
          </div>
          <div className="text-xs text-muted-foreground pt-4 border-t border-border/60 flex items-center justify-between gap-2">
            <span>
              Alerts: <span className="tabular">{formatBg(alertLow, units)}</span> · <span className="tabular">{formatBg(alertHigh, units)}</span> {units}
            </span>
            {sensor && (
              <Link
                to="/settings"
                className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${sensor.expired ? "border-red-500/40 sig-urgent" : "border-border text-muted-foreground"} hover:text-foreground tabular`}
                data-testid="sensor-pill"
                title={`${sensor.device_label}${sensor.serial ? " · " + sensor.serial : ""}`}
              >
                Sensor · Day {sensor.day}/{sensor.expected_days}
              </Link>
            )}
          </div>
          {predictions && (
            <div className="flex items-center gap-2 pt-3" data-testid="prediction-pills">
              <div className="flex-1 px-3 py-2 rounded-md border border-border/60 bg-secondary/40" data-testid="iob-pill">
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">IOB</div>
                <div className="font-display text-xl tabular">
                  {predictions.iob ?? 0}<span className="text-xs text-muted-foreground ml-1">U</span>
                </div>
              </div>
              <div className="flex-1 px-3 py-2 rounded-md border border-border/60 bg-secondary/40" data-testid="cob-pill">
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">COB</div>
                <div className="font-display text-xl tabular">
                  {predictions.cob ?? 0}<span className="text-xs text-muted-foreground ml-1">g</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main chart */}
        <div className="lg:col-span-3 lg:row-span-2 rounded-lg border border-border bg-card p-3 sm:p-4 lg:p-6 min-h-[340px]" data-testid="glucose-chart-card">
          <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
            <div className="font-display font-medium text-base sm:text-lg min-w-0 truncate">
              {selectedDate
                ? `Glucose · ${selectedDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                : `Glucose · ${RANGES.find(r => r.value === hours)?.label}`}
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {iobSeries.some((p) => p.iob > 0) && (
                <button
                  onClick={() => {
                    const v = !showIob;
                    setShowIob(v);
                    localStorage.setItem("gc.showIob", v ? "1" : "0");
                  }}
                  className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border whitespace-nowrap ${showIob ? "border-foreground text-foreground bg-secondary/60" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                  data-testid="iob-overlay-toggle"
                >
                  IOB {showIob ? "ON" : "OFF"}
                </button>
              )}
              <div className="text-xs text-muted-foreground tabular whitespace-nowrap">{entries.length} pts</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280} className="sm:!h-[320px]">
            <ComposedChart margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                type="number"
                domain={[window.since, window.until]}
                scale="time"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => formatTime(v, window.effectiveHours)}
                tickCount={8}
                interval="preserveStartEnd"
                minTickGap={40}
                allowDuplicatedCategory={false}
              />
              <YAxis domain={[yMin, yMax]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={yTick} width={44} />
              {showIob && iobSeries.length > 0 && (
                <YAxis
                  yAxisId="iob"
                  orientation="right"
                  domain={[0, "dataMax + 1"]}
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  width={34}
                  tickFormatter={(v) => v.toFixed(1)}
                  label={{ value: "IOB (U)", angle: 90, position: "insideRight", fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                />
              )}
              <ReferenceLine y={alertHigh} stroke={YELLOW} strokeDasharray="4 4" strokeOpacity={0.55} label={{ value: `High ${Math.round(alertHigh)}`, position: "insideTopRight", fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
              <ReferenceLine y={alertLow} stroke={RED} strokeDasharray="4 4" strokeOpacity={0.55} label={{ value: `Low ${Math.round(alertLow)}`, position: "insideBottomRight", fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
              {showIob && iobSeries.length > 0 && (
                <Area
                  yAxisId="iob"
                  data={iobSeries}
                  dataKey="iob"
                  type="monotone"
                  fill="hsl(var(--foreground))"
                  fillOpacity={0.09}
                  stroke="hsl(var(--foreground))"
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                  isAnimationActive={false}
                  name="IOB"
                />
              )}
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                formatter={(v, _key, item) => {
                  if (v == null) return null;
                  // The IOB Area passes name="IOB" — format as units, not mg/dL
                  if (item?.name === "IOB") return [`${Number(v).toFixed(2)} U`, "IOB"];
                  if (item?.name === "Predicted") return [`${formatBg(v, units)} ${units}`, "Predicted"];
                  return [`${formatBg(v, units)} ${units}`, "BG"];
                }}
                labelFormatter={(v) => new Date(v).toLocaleString()}
                labelStyle={{ color: "hsl(var(--foreground))" }}
              />
              {/* Three zone-specific Lines. Threshold-crossing points ensure
                  segments meet exactly at 70 mg/dL and 180 mg/dL. */}
              <Line data={zoneData.red} type="monotone" dataKey="sgv" stroke={RED} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: RED }} isAnimationActive={false} connectNulls={false} legendType="none" name="Low" />
              <Line data={zoneData.green} type="monotone" dataKey="sgv" stroke={GREEN} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: GREEN }} isAnimationActive={false} connectNulls={false} legendType="none" name="In range" />
              <Line data={zoneData.yellow} type="monotone" dataKey="sgv" stroke={YELLOW} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: YELLOW }} isAnimationActive={false} connectNulls={false} legendType="none" name="High" />
              {!selectedDate && iobSeries.some((p) => p.predicted_bg != null) && (
                <Line
                  data={iobSeries.filter((p) => p.predicted_bg != null).map((p) => ({ date: p.date, sgv: p.predicted_bg }))}
                  type="monotone"
                  dataKey="sgv"
                  stroke="hsl(var(--foreground))"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  strokeOpacity={0.55}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                  legendType="none"
                  name="Predicted"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          {entries.length === 0 && (
            <div className="text-center text-sm text-muted-foreground -mt-8 pointer-events-none" data-testid="no-data-msg">
              No readings in this range.
            </div>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <StatCard label="Time in Range" value={stats?.tir != null ? `${stats.tir}%` : "--"} sub="Target range" color="sig-in-range" testId="stat-tir" />
        <StatCard label="Time High" value={stats?.high_pct != null ? `${stats.high_pct}%` : "--"} sub={`> ${formatBg(targetHigh, units)}`} color="sig-high" testId="stat-high" />
        <StatCard label="Time Low" value={stats?.low_pct != null ? `${stats.low_pct}%` : "--"} sub={`< ${formatBg(targetLow, units)}`} color="sig-urgent" testId="stat-low" />
        <StatCard label="Average" value={stats?.avg != null ? formatBg(stats.avg, units) : "--"} sub={units} testId="stat-avg" />
        <StatCard label="GMI" value={stats?.gmi != null ? `${stats.gmi}%` : "--"} sub="Glucose Mgmt. Index" testId="stat-gmi" />
        <StatCard label="Est. HbA1c" value={stats?.hba1c != null ? `${stats.hba1c}%` : "--"} sub={`n=${stats?.count || 0}`} testId="stat-hba1c" />
      </div>

      {/* Watchers row */}
      {!targetUserId && watchers.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6" data-testid="watchers-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={18} />
              <div className="font-display font-medium text-lg">Following</div>
            </div>
            <Link to="/watchers" className="text-xs text-muted-foreground hover:text-foreground underline">Manage</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {watchers.map((w) => (
              <Link
                key={w.id}
                to={w.watched_user_id ? `/dashboard?patient=${w.watched_user_id}` : "#"}
                className={`p-4 rounded-md border border-border ${w.watched_user_id ? "hover:bg-secondary/60" : "opacity-60 cursor-not-allowed"}`}
                data-testid={`watcher-card-${w.id}`}
              >
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">{w.watched_user_id ? "Live" : "Pending"}</div>
                <div className="font-medium">{w.label}</div>
                <div className="text-xs text-muted-foreground truncate">{w.watched_email}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-center text-xs text-muted-foreground" data-testid="dashboard-loading">Loading…</div>}
    </div>
  );
}

function StatCard({ label, value, sub, color, testId }) {
  return (
    <div className="p-4 rounded-md border border-border bg-card" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl tabular mt-2 ${color || ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
